import cloneDeep from 'lodash.clonedeep';
import {
  AccessFunction, AccessFunctionFormData, AccessFunctionInterfaceFormData, AccessFunctionInterfaceFormSectionType
} from 'data/accessFunction/accessFunction.types';
import { FormTemplate, FormTemplateField } from 'data/template/template.types';
import { NodeData } from 'data/nodes/nodes.types';
import { getInterfaceFormTemplateSection, getSubsectionName } from './accessFunctionTemplate.adapter';
import { handleInterfacesSectionDeliveryViaFieldUpdate, handleCallContentDestinationIPFieldUpdate } from './handleDeliveryViaFieldUpdate';
import handleEncriptionViaFieldUpdate from './handleEncriptionViaFieldUpdate';
import { handleRolePriorityFieldUpdate } from './handleRolePriorityFieldUpdate';

// eslint-disable-next-line import/prefer-default-export
export function handleInterfacesFieldUpdate(
  template: FormTemplate | undefined,
  data: Partial<AccessFunctionFormData>,
  formSection: AccessFunctionInterfaceFormSectionType,
  interfaceId: string | undefined,
  fieldName: keyof AccessFunctionInterfaceFormData,
  fieldValue: unknown,
  xcipioNodes: NodeData[],
  tabIndex?: number,
  accessFunctions?: AccessFunction[]
): Partial<AccessFunctionFormData> {
  if (template == null) {
    return data;
  }

  let clonedData = cloneDeep(data);
  clonedData = handleInterfacesSectionDeliveryViaFieldUpdate(template, formSection, clonedData, fieldName, fieldValue);
  clonedData = handleEncriptionViaFieldUpdate(template, formSection, clonedData, fieldName, fieldValue, ['secinfoid']);
  clonedData = handleCallContentDestinationIPFieldUpdate(template, formSection, clonedData, fieldName, fieldValue, xcipioNodes);

  const formSubsectionName = getSubsectionName(data, template, formSection);

  clonedData[formSection] = clonedData[formSection]?.map((interfaceEntry) => {
    if ((formSection === 'provisioningInterfaces' && fieldName === 'mdf2NodeNameMultiSelect')
      || (formSection === 'provisioningInterfaces' && fieldName === 'mdf3NodeNameMultiSelect')
      || interfaceEntry.id === interfaceId) {
      return handleInterfaceFieldUpdate(
        template,
        formSection,
        formSubsectionName,
        interfaceEntry,
        fieldName,
        fieldValue,
        xcipioNodes,
      );
    }

    return interfaceEntry;
  });

  clonedData = handleRolePriorityFieldUpdate(template, formSection, fieldName, clonedData, fieldValue, tabIndex);

  if (formSection === 'eimsNetworkElement' && fieldName === 'parentid' && accessFunctions) {
    const parentAF = accessFunctions.filter((accessFunction) => accessFunction.tag === 'ERICSSON_LIMS').find(({ provisioningInterfaces }) => {
      const afid = provisioningInterfaces?.[0]?.options?.find(({ key }) => key === 'afid')?.value.toString()
      return afid === fieldValue;
    })
    if (parentAF) {
      clonedData.model = parentAF.model;
      clonedData.version = parentAF.version;
    }
  }

  if (formSection === 'eimsNetworkElement'
    && clonedData && clonedData.eimsNetworkElement
    && fieldName === 'netype'
    && (fieldValue === 'OV_SIPSERVER' || fieldValue === 'OV_MOWLAN' || fieldValue === 'OV_GPRS')) {
    clonedData.eimsNetworkElement.forEach((eimsNetworkElement) => {
      eimsNetworkElement.isnegroup = 'TRUE';
    });
  }

  return clonedData;
}

export function fieldAffectsOtherFieldsOnChange(formTemplateField: FormTemplateField | undefined): boolean {
  if (formTemplateField == null || formTemplateField.affectedFieldsOnChange == null) {
    return false;
  }

  return formTemplateField.affectedFieldsOnChange.length > 0;
}

/**
 * Sometimes when a field is changed, other field data needs to be set to a certain value.
 * This function set the data of dependent fields when value of a controlling field is changed.
 */
function handleInterfaceFieldUpdate(
  template: FormTemplate,
  formSection: AccessFunctionInterfaceFormSectionType,
  formSubsectionName: string,
  interfaceFormData: AccessFunctionInterfaceFormData,
  fieldName: keyof AccessFunctionInterfaceFormData,
  fieldValue: unknown,
  xcipioNodes: NodeData[],
) {
  setInterfaceFieldValue(interfaceFormData, fieldName, fieldValue);

  const templateSection = getInterfaceFormTemplateSection(template, formSection, formSubsectionName);
  const templateField = templateSection?.fields[fieldName];

  if (formSection === 'provisioningInterfaces' && fieldName === 'uri') {
    if (fieldAffectsOtherFieldsOnChange(templateField)) {
      interfaceFormData = setInterfaceValuesForUriValue(
        templateField?.affectedFieldsOnChange,
        interfaceFormData,
        fieldValue as string,
        template.key,
      );
    }
  }

  if (formSection === 'provisioningInterfaces' && fieldName === 'secinfoid' && (template.key === 'VRNT_SG' || template.key === 'COGNYTE')) {
    interfaceFormData = setInterfaceValuesForEncryptionValue(interfaceFormData, fieldValue as string);
  }

  if (formSection === 'provisioningInterfaces' && fieldName === 'mdf3NodeName' && template.key === 'PCRF') {
    if (fieldAffectsOtherFieldsOnChange(templateField)) {
      interfaceFormData = setInterfaceValuesForMdf3NodeNameValue(
        templateField?.affectedFieldsOnChange,
        interfaceFormData,
        fieldValue as string,
        xcipioNodes,
      );
    }
  }

  if (formSection === 'provisioningInterfaces' && fieldName === 'ccnodename') {
    if (fieldAffectsOtherFieldsOnChange(templateField)) {
      interfaceFormData = setInterfaceValuesForCcNodeNameValue(
        templateField?.affectedFieldsOnChange,
        interfaceFormData,
        fieldValue as string,
        xcipioNodes,
      );
    }
  }

  if (formSection === 'provisioningInterfaces' && fieldName === 'nodeNameType') {
    if (fieldAffectsOtherFieldsOnChange(templateField)) {
      interfaceFormData = setInterfaceValuesForNodeNameType(
        templateField?.affectedFieldsOnChange,
        interfaceFormData,
        fieldValue as string,
      );
    }
  }

  if (formSection === 'contentInterfaces' && fieldName === 'maxx2connsperaf') {
    if (fieldAffectsOtherFieldsOnChange(templateField)) {
      interfaceFormData = setInterfaceValuesForMaxx2ConnValue(
        templateField?.affectedFieldsOnChange,
        interfaceFormData,
        fieldValue as string,
      );
    }
  }

  if (formSection === 'dataInterfaces' && fieldName === 'tpkt') {
    if (fieldAffectsOtherFieldsOnChange(templateField)) {
      interfaceFormData = setInterfaceValuesForTPKTValue(
        templateField?.affectedFieldsOnChange,
        interfaceFormData,
        fieldValue as boolean,
      );
    }
  }

  if (formSection === 'dataInterfaces' && fieldName === 'ftpType') {
    if (fieldAffectsOtherFieldsOnChange(templateField)) {
      interfaceFormData = setInterfaceValuesForFtpTypeValue(
        templateField?.affectedFieldsOnChange,
        interfaceFormData,
        fieldValue as string,
      );
    }
  }

  if (formSection === 'dataInterfaces' && fieldName === 'deliveryFormat') {
    if (fieldAffectsOtherFieldsOnChange(templateField)) {
      interfaceFormData = setInterfaceValuesForDeliveryFormat(
        templateField?.affectedFieldsOnChange,
        interfaceFormData,
        fieldValue as string,
      );
    }
  }

  if (formSection === 'eimsNetworkElement' && fieldName === 'netype') {
    if (fieldAffectsOtherFieldsOnChange(templateField)) {
      interfaceFormData = setResVersionInterfaceValuesForNetType(
        templateField?.affectedFieldsOnChange,
        interfaceFormData,
        fieldValue as string,
      );
    }
  }

  /*
  if (formSection === 'dataInterfaces' && fieldName === 'interface') {
    if (fieldAffectsOtherFieldsOnChange(templateField)) {
      if (fieldValue === 'NIX1') {
        interfaceFormData.port = '11504';
      }

      if (fieldValue === 'NIX2') {
        interfaceFormData.port = '11503';
      }
    }
  }
  */

  if (template.key === 'COGNYTE' && formSection === 'monitoringCenters' && fieldName === 'cdtype') {
    if (fieldValue === 'FTP') {
      interfaceFormData.cdport = '21';
    } else {
      interfaceFormData.cdport = '';
    }
  }

  return interfaceFormData;
}

function setInterfaceFieldValue(
  interfaceFormData: any,
  fieldName: string,
  value: unknown,
): AccessFunctionInterfaceFormData {
  interfaceFormData[fieldName] = value;

  return interfaceFormData;
}

function setInterfaceValuesForUriValue(
  affectedFieldsOnChange: string[] | undefined,
  interfaceFormData: AccessFunctionInterfaceFormData,
  fieldValue: string,
  key: string,
): AccessFunctionInterfaceFormData {
  if (affectedFieldsOnChange == null) {
    return interfaceFormData;
  }

  if (affectedFieldsOnChange.includes('secinfoid')) {

    if (fieldValue.toLocaleLowerCase().startsWith('http:') && interfaceFormData.secinfoid === '' && key !== 'VRNT_SG' && key !== 'COGNYTE') {
      interfaceFormData.secinfoid = 'Tk9ORQ';
    } else if (fieldValue.toLocaleLowerCase().startsWith('http:') && interfaceFormData.secinfoid === 'Tk9ORQ' && (key === 'VRNT_SG' || key === 'COGNYTE')) {
      interfaceFormData.secinfoid = '';
      interfaceFormData.encryption = 'DISABLED';
    } else if (fieldValue.toLocaleLowerCase().startsWith('http:') && interfaceFormData.secinfoid === '' && (key === 'VRNT_SG' || key === 'COGNYTE')) {
      interfaceFormData.encryption = 'DISABLED';
    } else if (fieldValue.toLocaleLowerCase().startsWith('https:') && interfaceFormData.secinfoid === 'Tk9ORQ') {
      interfaceFormData.secinfoid = '';
      if (key === 'VRNT_SG' || key === 'COGNYTE') {
        interfaceFormData.encryption = 'ENABLED';
      }
    }
  }

  return interfaceFormData;
}

function setInterfaceValuesForMdf3NodeNameValue(
  affectedFieldsOnChange: string[] | undefined,
  interfaceFormData: AccessFunctionInterfaceFormData,
  fieldValue: string,
  xcipioNodes: NodeData[],
) {
  if (affectedFieldsOnChange == null) {
    return interfaceFormData;
  }

  if (affectedFieldsOnChange.includes('ccdestip')) {
    const node = xcipioNodes.find(({ nodeName }) => nodeName === fieldValue);

    if (node != null && node.ipAddress != null && node.ipAddress !== '') {
      interfaceFormData.ccdestip = node.ipAddress;
    }
  }
  return cloneDeep(interfaceFormData);
}

function setInterfaceValuesForCcNodeNameValue(
  affectedFieldsOnChange: string[] | undefined,
  interfaceFormData: AccessFunctionInterfaceFormData,
  fieldValue: string,
  xcipioNodes: NodeData[],
): AccessFunctionInterfaceFormData {
  if (affectedFieldsOnChange == null) {
    return interfaceFormData;
  }

  if (affectedFieldsOnChange.includes('mdf3NodeName')) {
    const ccPagNode = xcipioNodes.find(({ nodeName }) => nodeName === fieldValue);

    if (ccPagNode != null && ccPagNode.destNodeName != null && ccPagNode.destNodeName !== '') {
      interfaceFormData.mdf3NodeName = ccPagNode.destNodeName;
    }
  }

  return interfaceFormData;
}

function setInterfaceValuesForTPKTValue(
  affectedFieldsOnChange: string[] | undefined,
  interfaceFormData: AccessFunctionInterfaceFormData,
  fieldValue: boolean | undefined,
): AccessFunctionInterfaceFormData {
  if (affectedFieldsOnChange == null) {
    return interfaceFormData;
  }

  if (affectedFieldsOnChange.includes('x224headerpresent')) {
    if (fieldValue ?? true) {
      interfaceFormData.x224headerpresent = true;
    } else {
      interfaceFormData.x224headerpresent = false;
    }
  }

  return interfaceFormData;
}

function setInterfaceValuesForMaxx2ConnValue(
  affectedFieldsOnChange: string[] | undefined,
  interfaceFormData: AccessFunctionInterfaceFormData,
  fieldValue: string,
): AccessFunctionInterfaceFormData {
  if (affectedFieldsOnChange == null) {
    return interfaceFormData;
  }

  if (affectedFieldsOnChange.includes('destPort')) {
    if (+fieldValue > 4) {
      interfaceFormData.destPort = '0';
    }
  }

  return interfaceFormData;
}

function setInterfaceValuesForFtpTypeValue(
  affectedFieldsOnChange: string[] | undefined,
  interfaceFormData: AccessFunctionInterfaceFormData,
  fieldValue: string,
): AccessFunctionInterfaceFormData {
  if (affectedFieldsOnChange == null) {
    return interfaceFormData;
  }

  if (affectedFieldsOnChange.includes('secinfoid')) {
    if (fieldValue === 'FTP' && interfaceFormData.secinfoid === '') {
      interfaceFormData.secinfoid = 'Tk9ORQ';

    } else if (fieldValue === 'FTPS' && interfaceFormData.secinfoid === 'Tk9ORQ') {
      interfaceFormData.secinfoid = '';
    }
  }
  return interfaceFormData;
}

function setInterfaceValuesForEncryptionValue(
  interfaceFormData: AccessFunctionInterfaceFormData,
  fieldValue: string,
): AccessFunctionInterfaceFormData {
  if (fieldValue === 'Tk9ORQ' || fieldValue === '') {
    interfaceFormData.encryption = 'DISABLED';
    interfaceFormData.secinfoid = '';
  } else {
    interfaceFormData.encryption = 'ENABLED';
  }

  return interfaceFormData;
}

function setInterfaceValuesForDeliveryFormat(
  affectedFieldsOnChange: string[] | undefined,
  interfaceFormData: AccessFunctionInterfaceFormData,
  fieldValue: string,
): AccessFunctionInterfaceFormData {
  if (affectedFieldsOnChange == null) {
    return interfaceFormData;
  }

  if (fieldValue === 'TS33108') {
    interfaceFormData.naming = undefined;
    interfaceFormData.appProtocol = undefined;
    interfaceFormData.version = undefined;
  }
  return interfaceFormData;
}

function setResVersionInterfaceValuesForNetType(
  affectedFieldsOnChange: string[] | undefined,
  interfaceFormData: AccessFunctionInterfaceFormData,
  fieldValue: string,
): AccessFunctionInterfaceFormData {
  if (affectedFieldsOnChange == null) {
    return interfaceFormData;
  }

  if (fieldValue === 'MSC') {
    interfaceFormData.resversion = 'MSCR12';
  }
  if (fieldValue === 'HLR') {
    interfaceFormData.resversion = 'NONE';
  }
  if (fieldValue === 'GPRS' || fieldValue === 'OV_MOWLAN') {
    interfaceFormData.resversion = 'CGSN4';
  }
  if (fieldValue === 'OV_GPRS') {
    interfaceFormData.resversion = 'GGSN';
  }
  if (fieldValue === 'FIX') {
    interfaceFormData.resversion = 'TL5';
  }
  if (fieldValue === 'MOWLAN') {
    interfaceFormData.resversion = 'NONE';
  }
  if (fieldValue === 'SIPSERVER' || fieldValue === 'OV_SIPSERVER') {
    interfaceFormData.resversion = 'A-SBG15';
  }

  return interfaceFormData;
}

function setInterfaceValuesForNodeNameType(
  affectedFieldsOnChange: string[] | undefined,
  interfaceFormData: AccessFunctionInterfaceFormData,
  fieldValue: string,
): AccessFunctionInterfaceFormData {
  if (affectedFieldsOnChange == null) {
    return interfaceFormData;
  }

  if (fieldValue === 'mdf3NodeName') {
    interfaceFormData.ccnodename = '';
  } else if (fieldValue === 'ccnodename') {
    interfaceFormData.mdf3NodeName = '';
  }

  return interfaceFormData;
}
